/**
 * Row drag-and-drop: the arithmetic, kept away from the DOM (spec M1-B3 §8.1, §9 B1/B2).
 *
 * What monday does, from the capture: the **whole name cell** is the drag affordance
 * (`canDrag--zcZRZ` on `div.name-cell-component`), and the drop line is inset by
 * `--pulse-drag-line-left: 38px` — i.e. it starts after the group colour bar and the checkbox
 * gutter, not at the row's left edge `[trace:column-menu]`, `[B3§8.1]`.
 *
 * The grid is virtualised, so a library that owns the row list (dnd-kit's sortable) would have
 * to be told about rows that do not exist in the DOM. Instead this follows the pattern the
 * column headers and the view tabs already use in this codebase — the HTML5 drag API, which
 * needs no state outside the pointer — and this module owns the only part worth testing: given
 * "the pointer is over row X, in its top half", **which `(groupId, afterItemId)` is that**.
 *
 * `afterItemId` is the contract `BoardDataSource.reorderItem` takes (`null` = first in the
 * group), which is monday's `change_item_position(relative_to, position_relative_method)`
 * shape `[B3§5]`.
 */
import type { GroupId, ItemId } from '@elizmonday/shared';
import type { FlatRow } from './layout';

/** The MIME type the row drag puts on the dataTransfer, mirroring `text/ez-column`. */
export const ROW_DRAG_TYPE = 'text/ez-row';

/**
 * P-102 — the **group** drag's own type. A separate MIME type rather than a flag inside the
 * row payload, so a group drag can never be read as a row drag: the grid canvas accepts a drop
 * only for the type it handles, and the two drop targets overlap (a group header is both).
 */
export const GROUP_DRAG_TYPE = 'text/ez-group';

export interface RowDropTarget {
  readonly groupId: GroupId;
  /** The item the dragged row lands **after**; `null` means "first in the group". */
  readonly afterItemId: ItemId | null;
}

/** Where the drop indicator is painted: above or below the row it is hovering. */
export type DropEdge = 'above' | 'below';

export interface RowDropHover {
  /** The row being hovered — an item row, a group header, or a group's add-item row. */
  readonly rowKey: string;
  readonly edge: DropEdge;
  readonly target: RowDropTarget;
}

/** The ordered item ids of each group, as the grid currently shows them. */
export function itemsByGroup(rows: readonly FlatRow[]): Map<string, ItemId[]> {
  const out = new Map<string, ItemId[]>();
  for (const row of rows) {
    if (row.kind !== 'row' || !row.itemId) continue;
    const list = out.get(row.groupId) ?? [];
    list.push(row.itemId);
    out.set(row.groupId, list);
  }
  return out;
}

/**
 * Hovering an **item row**: the top half inserts before it, the bottom half after it.
 *
 * "Before X" is expressed as "after X's predecessor", so a drop at the very top of a group is
 * `afterItemId: null` and needs no separate case downstream.
 */
export function dropOnItem(
  rows: readonly FlatRow[],
  overItemId: ItemId,
  edge: DropEdge,
): RowDropTarget | null {
  const groups = itemsByGroup(rows);
  for (const [groupId, items] of groups) {
    const index = items.indexOf(overItemId);
    if (index < 0) continue;
    return {
      groupId: groupId as GroupId,
      afterItemId: edge === 'below' ? overItemId : (items[index - 1] ?? null),
    };
  }
  return null;
}

/** Hovering a **group header**: the row lands at the top of that group. */
export function dropOnGroupHeader(groupId: GroupId): RowDropTarget {
  return { groupId, afterItemId: null };
}

/**
 * Hovering a group's **add-item row**: the row lands at the bottom of that group. This is the
 * only way to reach the last position of a collapsed or empty group.
 */
export function dropOnGroupEnd(rows: readonly FlatRow[], groupId: GroupId): RowDropTarget {
  const items = itemsByGroup(rows).get(groupId) ?? [];
  return { groupId, afterItemId: items[items.length - 1] ?? null };
}

/**
 * Is this drop a no-op? Dragging a row onto its own edges must not issue a mutation — monday
 * writes no activity row for a reorder that changes nothing, and neither should we (Q154).
 */
export function isNoOpDrop(
  rows: readonly FlatRow[],
  draggedItemId: ItemId,
  target: RowDropTarget,
): boolean {
  if (target.afterItemId === draggedItemId) return true;
  const items = itemsByGroup(rows).get(String(target.groupId)) ?? [];
  const current = items.indexOf(draggedItemId);
  if (current < 0) return false; // a different group: always a real move
  const desired = target.afterItemId === null ? 0 : items.indexOf(target.afterItemId) + 1;
  // Removing the row first shifts every later index down by one.
  return desired === current || desired === current + 1;
}

/**
 * **P-103** — what a drag actually carries.
 *
 * monday's rule, as far as the captures show it: a row that is part of the current selection
 * drags the **whole** selection, and a row that is not drags itself and drops the selection.
 * The ids come back in the order the **board** has them, not the order they were ticked, so
 * the selection keeps its relative order wherever it lands (q500 — the reference gesture is in
 * no capture; this is the conservative reading P-103 records).
 */
export function draggedItemIds(
  rows: readonly FlatRow[],
  draggedItemId: ItemId,
  selectedItemIds: readonly ItemId[],
): ItemId[] {
  if (!selectedItemIds.some((id) => String(id) === String(draggedItemId))) return [draggedItemId];
  const selected = new Set(selectedItemIds.map(String));
  const ordered: ItemId[] = [];
  for (const row of rows) {
    if (row.kind !== 'row' || !row.itemId) continue;
    if (selected.has(String(row.itemId))) ordered.push(row.itemId);
  }
  return ordered.length > 0 ? ordered : [draggedItemId];
}

/**
 * The drop target seen by a **set** of rows: the anchor may itself be one of the rows being
 * moved (dropping a selection onto its own middle), and a row cannot land after itself, so
 * the anchor walks back to the first row that is not moving. `null` — the top of the group —
 * is where it ends up when they all are.
 */
export function targetForMany(
  rows: readonly FlatRow[],
  movingItemIds: readonly ItemId[],
  target: RowDropTarget,
): RowDropTarget {
  const moving = new Set(movingItemIds.map(String));
  if (target.afterItemId === null || !moving.has(String(target.afterItemId))) return target;
  const items = itemsByGroup(rows).get(String(target.groupId)) ?? [];
  let index = items.indexOf(target.afterItemId);
  while (index >= 0 && moving.has(String(items[index]))) index -= 1;
  return { groupId: target.groupId, afterItemId: index >= 0 ? (items[index] ?? null) : null };
}

/** Every row of the selection is already exactly where the drop would put it. */
export function isNoOpDropForMany(
  rows: readonly FlatRow[],
  movingItemIds: readonly ItemId[],
  target: RowDropTarget,
): boolean {
  if (movingItemIds.length === 0) return true;
  const items = itemsByGroup(rows).get(String(target.groupId)) ?? [];
  const desired = target.afterItemId === null ? 0 : items.indexOf(target.afterItemId) + 1;
  if (desired < 0) return false;
  const wanted = movingItemIds.map(String);
  const actual = items.slice(desired, desired + wanted.length).map(String);
  return actual.length === wanted.length && actual.every((id, at) => id === wanted[at]);
}

/** Top or bottom half of the hovered box, from the pointer's client Y. */
export function edgeOf(box: { top: number; height: number }, clientY: number): DropEdge {
  return clientY - box.top < box.height / 2 ? 'above' : 'below';
}

/* ── the keyboard equivalent (P-106, `[B4§A1]`) ──────────────────────────── */

/** One position a row can be moved to by keyboard: the drop target plus where to paint it. */
export interface RowSlot {
  readonly target: RowDropTarget;
  readonly rowKey: string;
  readonly edge: DropEdge;
  /** The 1-based position inside its group, and the group's own row count, for the announce. */
  readonly index: number;
  readonly of: number;
}

/**
 * Every position the dragged row could take, in board order — the top of each group, then
 * after each of its other rows.
 *
 * The HTML5 drag API is pointer-only by definition (a browser starts `dragstart` from a
 * pointer gesture, never from a key), so the keyboard reorder is not the same gesture with a
 * different input: it is this list plus `Space` / arrows / `Space`. `[B4§A1]` requires every
 * interactive element to be reachable by keyboard, and reordering is an interaction.
 */
export function rowSlots(rows: readonly FlatRow[], draggedItemId: ItemId): RowSlot[] {
  const byGroup = itemsByGroup(rows);
  const order: string[] = [];
  for (const row of rows) {
    if (row.kind !== 'group-header') continue;
    if (!order.includes(row.groupId)) order.push(row.groupId);
  }
  for (const groupId of byGroup.keys()) if (!order.includes(groupId)) order.push(groupId);

  const slots: RowSlot[] = [];
  for (const groupId of order) {
    const items = (byGroup.get(groupId) ?? []).filter(
      (itemId) => String(itemId) !== String(draggedItemId),
    );
    const of = items.length + 1;
    slots.push({
      target: { groupId: groupId as GroupId, afterItemId: null },
      rowKey: `group:${groupId}`,
      edge: 'below',
      index: 1,
      of,
    });
    items.forEach((itemId, at) => {
      slots.push({
        target: { groupId: groupId as GroupId, afterItemId: itemId },
        rowKey: `item:${itemId}`,
        edge: 'below',
        index: at + 2,
        of,
      });
    });
  }
  return slots;
}

/** Which slot the row is in right now — where a keyboard drag starts from. */
export function slotOf(slots: readonly RowSlot[], rows: readonly FlatRow[], itemId: ItemId): number {
  const byGroup = itemsByGroup(rows);
  for (const [groupId, items] of byGroup) {
    const at = items.findIndex((candidate) => String(candidate) === String(itemId));
    if (at < 0) continue;
    const previous = items[at - 1] ?? null;
    const found = slots.findIndex(
      (slot) =>
        String(slot.target.groupId) === groupId &&
        String(slot.target.afterItemId ?? '') === String(previous ?? ''),
    );
    return found < 0 ? 0 : found;
  }
  return 0;
}
